// Thin `PdsClient` adapter over @atproto/api, plus an app-password login helper (§15, §C.1).
//
// This is the only file that touches a network library; publish/rollback depend solely on the
// `PdsClient` seam (types.ts), so they are testable against a fake and the client is swappable.
// The adapter transports record values as verbatim JSON — the PDS lex-decodes and dCBOR42-encodes
// them, matching the CIDs precomputed in records.ts (cross-checked in records.record-cid.test.ts).
import { Agent, AtpAgent, type ComAtprotoRepoApplyWrites, lexToJson } from "@atproto/api";
import { atpServiceAuthMinter, type ServiceAuthMinter } from "./service-auth.ts";
import {
  type ApplyWrite,
  type CommitRef,
  type FetchedRecord,
  type PdsClient,
  type RepoRecordLister,
  SwapFailedError,
} from "./types.ts";

// Fallback upload encoding only. `uploadBlobs` always passes each blob's canonical mimeType (§7.2),
// which a conforming PDS content-sniffs, stores, and enforces against the record's blob ref; this
// default is a last resort for a caller that supplies no type.
const BLOB_ENCODING = "application/octet-stream";

/** True for the XRPC error a PDS returns when a swapCommit/swapRecord CAS guard fails. */
function isSwapError(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    "error" in err &&
    (err as { error?: unknown }).error === "InvalidSwap"
  );
}

/** True for the XRPC error(s) a PDS returns when a record does not exist. */
function isNotFoundError(err: unknown): boolean {
  if (typeof err !== "object" || err === null) return false;
  const e = err as { error?: unknown; message?: unknown };
  if (e.error === "RecordNotFound") return true;
  return typeof e.message === "string" && /could not locate record|not found/i.test(e.message);
}

/** Map an internal {@link ApplyWrite} to the `com.atproto.repo.applyWrites` write union. */
function toAtWrite(w: ApplyWrite): Record<string, unknown> {
  const $type = `com.atproto.repo.applyWrites#${w.action}`;
  return w.action === "delete"
    ? { $type, collection: w.collection, rkey: w.rkey }
    : { $type, collection: w.collection, rkey: w.rkey, value: w.value };
}

/**
 * Wrap an authenticated {@link Agent} as a {@link PdsClient}. The agent must already be logged
 * in (its DID is read via `assertDid`). Custom-lexicon records are written with `validate: false`
 * so the PDS does not reject NSIDs it has no lexicon for.
 *
 * Typed to just the two members this adapter uses (`assertDid`, `com.atproto.*`) rather than a whole
 * `AtpAgent`. That lets both an OAuth `Agent` (from `@atproto/oauth-client-browser`, `new Agent(session)`)
 * and an app-password `AtpAgent` flow in with no cast — sidestepping an `@atproto/api` type wart where
 * `AtpAgent` is not structurally assignable to `Agent` (its `sessionManager.did` is `string | undefined`
 * vs the base's `string`) under `exactOptionalPropertyTypes`.
 */
export function atpClient(agent: Pick<Agent, "assertDid" | "com">): PdsClient {
  const did = agent.assertDid;
  return {
    did,

    async uploadBlob(bytes, contentType = BLOB_ENCODING) {
      const res = await agent.com.atproto.repo.uploadBlob(bytes, { encoding: contentType });
      const { blob } = res.data;
      return { cid: blob.ref.toString(), size: blob.size };
    },

    async getBlob(cid) {
      try {
        const res = await agent.com.atproto.sync.getBlob({ did, cid });
        return res.data;
      } catch (err) {
        // Stock PDSes return InvalidRequest/"Blob not found" for unanchored temporary uploads.
        // Do NOT classify timeouts, 401s, endpoint failures or rate limits as missing content.
        const e = err as { error?: unknown; message?: unknown } | null;
        if (e?.error === "BlobNotFound" ||
          (e?.error === "InvalidRequest" && e.message === "Blob not found")) return null;
        throw err;
      }
    },

    async getLatestCommit(): Promise<CommitRef> {
      const res = await agent.com.atproto.sync.getLatestCommit({ did });
      return { cid: res.data.cid, rev: res.data.rev };
    },

    async applyWrites({ writes, swapCommit }) {
      try {
        const res = await agent.com.atproto.repo.applyWrites({
          repo: did,
          validate: false,
          // Adapter boundary: our records use custom lexicons, so we hand the atproto client
          // pre-shaped `#create/#update/#delete` write objects and let it transport them as JSON.
          writes: writes.map(toAtWrite) as ComAtprotoRepoApplyWrites.InputSchema["writes"],
          ...(swapCommit !== undefined ? { swapCommit } : {}),
        });
        const commit = res.data.commit;
        return commit ? { commit: { cid: commit.cid, rev: commit.rev } } : {};
      } catch (err) {
        if (isSwapError(err)) throw new SwapFailedError();
        throw err;
      }
    },

    async getRecord(collection, rkey): Promise<FetchedRecord | null> {
      try {
        const res = await agent.com.atproto.repo.getRecord({ repo: did, collection, rkey });
        // @atproto/api lex-DECODES the record value (cid-links → multiformats CID objects, blobs →
        // BlobRef). `FetchedRecord.value` is contractually raw IPLD-JSON ({ $link } / { $type:'blob' }),
        // which is what the CID recompute paths (and the gateway's fetcher) require — so re-serialize
        // it with lexToJson. Without this, any consumer that re-derives a CID from `value` (e.g. the
        // publish read-back in §9.2) sees `value.cid.$link === undefined` and mis-reports the record.
        return { uri: res.data.uri, cid: res.data.cid ?? "", value: lexToJson(res.data.value) };
      } catch (err) {
        if (isNotFoundError(err)) return null;
        throw err;
      }
    },

    async putRecord({ collection, rkey, value, swapRecord, swapCommit }) {
      try {
        const res = await agent.com.atproto.repo.putRecord({
          repo: did,
          collection,
          rkey,
          record: value as Record<string, unknown>,
          validate: false,
          ...(swapRecord !== undefined ? { swapRecord } : {}),
          ...(swapCommit !== undefined ? { swapCommit } : {}),
        });
        return { uri: res.data.uri, cid: res.data.cid };
      } catch (err) {
        if (isSwapError(err)) throw new SwapFailedError();
        throw err;
      }
    },
  };
}

/**
 * Wrap the SAME authenticated agent as a {@link RepoRecordLister} — the read-only enumeration seam
 * the NSID migration walks a repo with. Separate from {@link atpClient} because it is a separate
 * interface, not because it is a separate credential: both take the agent the caller already has,
 * so no new auth path exists for enumeration.
 *
 * Values are re-serialized with `lexToJson` for the same reason `getRecord` does it: `@atproto/api`
 * lex-decodes cid-links into CID objects, and every consumer here re-derives a record CID from the
 * raw IPLD-JSON shape.
 */
export function atpRecordLister(agent: Pick<Agent, "assertDid" | "com">): RepoRecordLister {
  const did = agent.assertDid;
  return {
    async listRecords({ collection, limit, cursor }) {
      const res = await agent.com.atproto.repo.listRecords({
        repo: did,
        collection,
        ...(limit !== undefined ? { limit } : {}),
        ...(cursor !== undefined ? { cursor } : {}),
      });
      return {
        records: res.data.records.map((r) => ({
          uri: r.uri,
          cid: r.cid,
          value: lexToJson(r.value),
        })),
        ...(res.data.cursor !== undefined ? { cursor: res.data.cursor } : {}),
      };
    },
  };
}

/** Credentials for an app-password session (§15 non-interactive path). */
export interface AppPasswordLogin {
  /** PDS / entryway service URL, e.g. `https://bsky.social`. */
  service: string;
  /** Handle or DID. */
  identifier: string;
  /** An app password (NOT the account password). */
  password: string;
}

/** One app-password login, exposing both capabilities it confers: PDS writes and gateway credentials. */
export interface PdsSession {
  /** The repo write/read seam (§7, §8) — what `publish` and `rollback` take. */
  client: PdsClient;
  /** The §10.1 gateway credential seam: mints service-auth JWTs for this account. */
  minter: ServiceAuthMinter;
}

/**
 * Log in with an app password and return a ready {@link PdsClient} (§15). Credentials are held by
 * the returned agent's session and never leave the client — the gateway is never in the auth path.
 */
export async function loginWithAppPassword(creds: AppPasswordLogin): Promise<PdsClient> {
  return (await loginSessionWithAppPassword(creds)).client;
}

/**
 * Log in with an app password and return the full {@link PdsSession} (§15). Same single login as
 * {@link loginWithAppPassword}, which is defined in terms of this — a caller that also needs a
 * gateway credential (a §11.1 slug claim after a publish) gets both from one authentication rather
 * than logging in twice.
 */
export async function loginSessionWithAppPassword(creds: AppPasswordLogin): Promise<PdsSession> {
  const agent = new AtpAgent({ service: creds.service });
  await agent.login({ identifier: creds.identifier, password: creds.password });
  return { client: atpClient(agent), minter: atpServiceAuthMinter(agent) };
}
